import { useEffect, useState } from "react";
import api from "../../lib/api";
import { ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { TrendingUp, Loader2, ChevronRight, FileText, FileSpreadsheet, Download } from "lucide-react";

const METRIC_OPTIONS = [
  { key: "revenue", label: "Revenus (XAF)", color: "#0A2540", type: "line", fmt: (v) => `${(v || 0).toLocaleString()} XAF` },
  { key: "bookings", label: "Réservations", color: "#FACC15", type: "bar", fmt: (v) => (v || 0).toLocaleString() },
  { key: "signups", label: "Nouveaux inscrits", color: "#10B981", type: "bar", fmt: (v) => (v || 0).toLocaleString(), isCombined: true },
  { key: "tft_amount", label: "TFT rechargé", color: "#3B82F6", type: "line", fmt: (v) => `${(v || 0).toLocaleString()} TFT` },
];

const PERIODS = [
  { value: 7, label: "7 jours" },
  { value: 30, label: "30 jours" },
  { value: 90, label: "90 jours" },
];

function TrendTooltip({ active, payload, label, metric }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-[#CBD5E1] rounded-lg p-2 shadow-md text-xs">
      <p className="font-semibold text-[#0A2540] mb-1">{label}</p>
      {payload.map((p) => (
        <p key={p.dataKey} className="text-[#64748B]">
          <span className="inline-block w-2 h-2 rounded-full mr-1.5" style={{ backgroundColor: p.color }} />
          {p.name}: <span className="font-semibold text-[#0A2540]">{metric.fmt ? metric.fmt(p.value) : p.value}</span>
        </p>
      ))}
    </div>
  );
}

export default function TrendChart() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [metric, setMetric] = useState(METRIC_OPTIONS[0]);
  const [days, setDays] = useState(7);
  const [exporting, setExporting] = useState(null); // "xlsx" | "pdf" | null
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/admin/stats/trend?days=${days}`)
      .then(({ data }) => { if (alive) setData(data.series || []); })
      .catch(() => { if (alive) setData([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [days]);

  const totalForMetric = data.reduce((sum, d) => {
    if (metric.isCombined) return sum + (d.new_drivers || 0) + (d.new_passengers || 0);
    return sum + (d[metric.key] || 0);
  }, 0);

  const handleExport = async (fmt) => {
    setMenuOpen(false);
    setExporting(fmt);
    try {
      const res = await api.get(`/admin/stats/export?days=${days}&format=${fmt}`, { responseType: "blob" });
      const blob = new Blob([res.data], {
        type: fmt === "pdf" ? "application/pdf" : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const today = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      a.download = `trigotour_stats_${days}j_${today}.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert("Erreur lors de l'export: " + (e.response?.data?.detail || e.message));
    } finally {
      setExporting(null);
    }
  };

  // Tick label thinning when 30/90 days to avoid overlap
  const xTickInterval = days <= 7 ? 0 : days <= 30 ? Math.floor(days / 10) : Math.floor(days / 12);

  return (
    <div className="bg-white rounded-xl border border-[#CBD5E1] p-5" data-testid="admin-trend-chart">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-[#0A2540]" />
          <h3 className="text-sm font-bold text-[#0A2540] uppercase tracking-wide" style={{ fontFamily: 'Outfit' }}>
            Évolution
          </h3>
        </div>

        {/* Period selector */}
        <div className="flex items-center gap-2">
          <div className="flex gap-1 bg-[#F8FAFC] border border-[#CBD5E1] rounded-full p-0.5" data-testid="trend-period-selector">
            {PERIODS.map((p) => (
              <button
                key={p.value}
                onClick={() => setDays(p.value)}
                className={`px-3 py-1 text-[11px] font-semibold rounded-full transition-colors ${days === p.value ? "bg-[#0A2540] text-white" : "text-[#64748B] hover:text-[#0A2540]"}`}
                data-testid={`trend-period-${p.value}`}
              >
                {p.label}
              </button>
            ))}
          </div>

          {/* Export menu */}
          <div className="relative">
            <button
              onClick={() => setMenuOpen((o) => !o)}
              disabled={!!exporting || loading}
              className="inline-flex items-center gap-1.5 px-3 h-8 rounded-full bg-[#0A2540] text-white text-[11px] font-semibold hover:bg-[#081b2e] disabled:opacity-50"
              data-testid="trend-export-btn"
            >
              {exporting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />} Exporter
            </button>
            {menuOpen && (
              <>
                <div className="fixed inset-0 z-30" onClick={() => setMenuOpen(false)} />
                <div className="absolute right-0 mt-1 w-48 bg-white border border-[#CBD5E1] rounded-lg shadow-lg z-40 overflow-hidden" data-testid="trend-export-menu">
                  <button
                    onClick={() => handleExport("xlsx")}
                    className="w-full px-3 py-2 text-left text-xs font-medium text-[#0A2540] hover:bg-[#F8FAFC] flex items-center gap-2"
                    data-testid="trend-export-xlsx"
                  >
                    <FileSpreadsheet className="w-3.5 h-3.5 text-[#10B981]" /> Excel (.xlsx)
                  </button>
                  <button
                    onClick={() => handleExport("pdf")}
                    className="w-full px-3 py-2 text-left text-xs font-medium text-[#0A2540] hover:bg-[#F8FAFC] flex items-center gap-2 border-t border-[#E2E8F0]"
                    data-testid="trend-export-pdf"
                  >
                    <FileText className="w-3.5 h-3.5 text-[#EF4444]" /> PDF (.pdf)
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Metric tabs */}
      <div className="flex flex-wrap gap-1.5 mb-3" data-testid="trend-metric-tabs">
        {METRIC_OPTIONS.map((m) => (
          <button
            key={m.key}
            onClick={() => setMetric(m)}
            className={`px-3 py-1 text-[11px] font-semibold rounded-full transition-colors ${metric.key === m.key ? "bg-[#FACC15] text-[#0A2540]" : "bg-[#F8FAFC] text-[#64748B] border border-[#CBD5E1] hover:border-[#FACC15]"}`}
            data-testid={`trend-tab-${m.key}`}
          >
            {m.label}
          </button>
        ))}
      </div>

      <div className="flex items-baseline gap-2 mb-3">
        <p className="text-2xl font-bold text-[#0A2540]" style={{ fontFamily: 'Outfit' }} data-testid="trend-total">
          {metric.fmt(totalForMetric)}
        </p>
        <p className="text-xs text-[#64748B]">
          Total sur les {days} derniers jours <ChevronRight className="inline w-3 h-3" />
        </p>
      </div>

      <div className="h-56" data-testid="trend-chart-container">
        {loading ? (
          <div className="flex items-center justify-center h-full"><Loader2 className="w-5 h-5 animate-spin text-[#0A2540]" /></div>
        ) : data.length === 0 ? (
          <div className="flex items-center justify-center h-full text-xs text-[#64748B]">Aucune donnée disponible</div>
        ) : metric.type === "bar" && metric.isCombined ? (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="label" stroke="#64748B" fontSize={10} interval={xTickInterval} />
              <YAxis stroke="#64748B" fontSize={11} allowDecimals={false} />
              <Tooltip content={<TrendTooltip metric={metric} />} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="new_passengers" stackId="a" fill="#10B981" name="Passagers" radius={[0, 0, 0, 0]} />
              <Bar dataKey="new_drivers" stackId="a" fill="#FACC15" name="Chauffeurs" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : metric.type === "bar" ? (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="label" stroke="#64748B" fontSize={10} interval={xTickInterval} />
              <YAxis stroke="#64748B" fontSize={11} allowDecimals={false} />
              <Tooltip content={<TrendTooltip metric={metric} />} />
              <Bar dataKey={metric.key} fill={metric.color} name={metric.label} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="label" stroke="#64748B" fontSize={10} interval={xTickInterval} />
              <YAxis stroke="#64748B" fontSize={11} tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} />
              <Tooltip content={<TrendTooltip metric={metric} />} />
              <Line type="monotone" dataKey={metric.key} stroke={metric.color} strokeWidth={2.5} dot={days <= 30 ? { r: 3, fill: metric.color } : false} activeDot={{ r: 5 }} name={metric.label} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
